import type { JSX } from "react";

import fyndLoaderGif from "../../assets/gif/fynd_one_logo.gif";
import { cn } from "../../lib/cn.js";

const SIZE_PX: Record<NonNullable<FyndLoaderProps["size"]>, number> = {
  sm: 32,
  md: 64,
  lg: 96,
  xl: 128,
};

export interface FyndLoaderProps {
  /** Display size of the looping mark. Defaults to `md` (64px). */
  size?: "sm" | "md" | "lg" | "xl";
  /** Optional caption rendered under the mark, e.g. "Loading captures…". */
  label?: string;
  /** Extra classes for the outer wrapper (use to control alignment/spacing). */
  className?: string;
}

/**
 * Brand loader — the Fynd One mark as a looping GIF.
 *
 * Single source of truth for in-app waiting states. Prefer this over Lucide's
 * spinner everywhere a user is waiting on data, a route to mount, or a slow
 * server action. The GIF is ~30 KB and animates on its own (no CSS), so it
 * paints in one frame and doesn't compete with React/Motion timelines.
 *
 * Lucide's `Loader2` is still appropriate for small inline affordances inside
 * buttons / chips / table cells where a 16-24px mark fits the typography
 * rhythm — this component is for everything else.
 */
export function FyndLoader({
  size = "md",
  label,
  className,
}: FyndLoaderProps): JSX.Element {
  const px = SIZE_PX[size];
  return (
    <div
      className={cn("flex flex-col items-center justify-center gap-3", className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <img
        src={fyndLoaderGif}
        alt=""
        width={px}
        height={px}
        style={{ width: px, height: px }}
        className="select-none"
        draggable={false}
      />
      {label ? (
        <span className="text-sm text-muted-foreground">{label}</span>
      ) : (
        <span className="sr-only">Loading…</span>
      )}
    </div>
  );
}
